/**
 * Type definitions for the /naighbourhood-description endpoint (Google Places path)
 * and shared domain types (scores, tags, aggregated data, request/response contracts).
 */

export enum ScoreType {
  Transit = 'transit',
  Playground = 'playground',
  Schools = 'schools',
  Shopping = 'shopping',
  Restaurants = 'restaurants',
  Education = 'education',
}

export enum TagType {
  Family = 'family',
  Transit = 'transit',
  Schools = 'schools',
  Green = 'green',
  Quiet = 'quiet',
  Lively = 'lively',
}

export interface LocationScore {
  id: string;
  name: string;
  value: number;
  description: string;
  type: ScoreType;
}

export interface NeighbourhoodTag {
  id: string;
  label: string;
  type: TagType;
}

export interface NeighbourhoodData {
  name: string;
  description: string;
  tags: NeighbourhoodTag[];
  scores: LocationScore[];
}

export interface AggregatedNeighbourhoodData extends NeighbourhoodData {
  // AI-enhanced fields
  aiInsights?: NeighbourhoodInsights;
  // Macro economic data (now from AI insights)
  macroData?: any;
  // Google Places data for map integration
  googlePlacesData?: {
    restaurants: any[];
    schools: any[];
    parks: any[];
    transit: any[];
    shopping: any[];
  };
  dataSource: 'ai-only' | 'mock';
  lastUpdated: string;
}

export interface POIContext {
  supermarket?: string[];
  pharmacy?: string[];
  station?: string[];
  school?: string[];
  park?: string[];
  playground?: string[];
  kindergarten?: string[];
  sports_centre?: string[];
  cafe?: string[];
  coworking?: string[];
  university?: string[];
  library?: string[];
  fast_food?: string[];
}

// AWS Lambda API Request/Response interfaces
export interface NeighbourhoodApiRequest {
  listingId: string;
  location: {
    country: string;
    postalCode?: string;
    locality: string;
    latitude: number;
    longitude: number;
    region?: string;
  };
  persona: 'Family' | 'Professional' | 'Student';
  locale?: string;
  objectDescription?: string;
  enableWebSearch?: boolean;
  googlePlacesContext?: {
    nearbyRestaurants?: string[];
    nearbySchools?: string[];
    nearbyParks?: string[];
    nearbyTransit?: string[];
    nearbyShopping?: string[];
  };
  poiContext?: POIContext;
  refinementAnswers?: Record<string, string>;
}

export interface NeighbourhoodApiResponse {
  description: string;
  atmosphere: string;
  lifestyleTags: string[];
  idealFor: Array<{
    type: string;
    score: number;
    reason: string;
  }>;
  highlights: string[];
  transportHub: string;
  greenSpaceQuality: 'Excellent' | 'Good' | 'Moderate' | 'Limited';
  environmentalScores?: {
    noiseLevel: {
      dayNoise: number;
      railNoise: number;
      streetNoise: number;
      overallQuietness: number;
      description: string;
    };
    transitScore: number;
    playgroundScore: number;
  };
  marketData?: {
    estimatedPricePerSqm: number;
    priceChange5Year: number;
    marketTemperature: string;
    rentalYield: number;
    vacancyRate: number;
    investmentScore: number;
  };
  taxData?: {
    estimatedTotalTaxRate: number;
    canton: string;
    taxFriendly: boolean;
    comparisonToAverage: string;
  };
}

export interface NeighbourhoodInsights {
  description: string;
  atmosphere: string;
  lifestyleTags: string[];
  idealFor: Array<{
    persona: string;
    score: number;
    reasoning: string;
  }>;
  highlights: string[];
  transportHub: {
    quality: 'excellent' | 'good' | 'moderate' | 'poor';
    description: string;
  };
  greenSpace: {
    quality: 'excellent' | 'good' | 'moderate' | 'limited';
    description: string;
  };
  environmentalScores?: {
    noiseLevel: {
      dayNoise: number;
      railNoise: number;
      streetNoise: number;
      overallQuietness: number;
      description: string;
    };
    transitScore: number;
    playgroundScore: number;
  };
  marketData?: {
    estimatedPricePerSqm: number;
    priceChange5Year: number;
    marketTemperature: string;
    rentalYield: number;
    vacancyRate: number;
    investmentScore: number;
  };
  taxData?: {
    estimatedTotalTaxRate: number;
    canton: string;
    taxFriendly: boolean;
    comparisonToAverage: string;
  };
}
